import React, { createContext, useContext, useState, ReactNode } from 'react';
import * as XLSX from 'xlsx';

export interface UsageReport {
  id: string;
  dateOfUse: string;
  timeOfUse: string;
  expectedPension: number;
  age: number;
  gender: 'male' | 'female';
  salaryAmount: number;
  includeSickLeave: boolean;
  accumulatedFunds: number;
  actualPension: number;
  realPension: number;
  postalCode?: string;
}

interface AdminContextType {
  usageReports: UsageReport[];
  addUsageReport: (report: Omit<UsageReport, 'id'>) => void;
  exportToExcel: () => void;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

interface AdminProviderProps {
  children: ReactNode;
}

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
  const [usageReports, setUsageReports] = useState<UsageReport[]>([]);

  const addUsageReport = (report: Omit<UsageReport, 'id'>) => {
    const newReport: UsageReport = {
      ...report,
      id: Date.now().toString()
    };
    setUsageReports(prev => [...prev, newReport]);
  };

  const exportToExcel = () => {
    if (usageReports.length === 0) {
      alert('Brak danych do eksportu');
      return;
    }

    // Prepare data for Excel
    const excelData = usageReports.map(report => ({
      'ID': report.id,
      'Data użycia': report.dateOfUse,
      'Godzina': report.timeOfUse,
      'Wiek': report.age,
      'Płeć': report.gender === 'male' ? 'Mężczyzna' : 'Kobieta',
      'Wynagrodzenie': report.salaryAmount,
      'Zwolnienia': report.includeSickLeave ? 'Tak' : 'Nie',
      'Zgromadzone środki': report.accumulatedFunds,
      'Oczekiwana emerytura': report.expectedPension,
      'Rzeczywista emerytura': report.actualPension,
      'Emerytura realna': report.realPension,
      'Kod pocztowy': report.postalCode || 'N/A'
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Raporty użycia');

    // Generate filename with current date
    const filename = `raport_symulacji_${new Date().toLocaleDateString('pl-PL').replace(/\./g, '-')}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  const value = {
    usageReports,
    addUsageReport,
    exportToExcel
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
